'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { SignInAccountCreationConfirmation } from '@/app_components/SignIn/Confirmations/SignInAccountCreationConfirmation';
import { SignInPasswordlessConfirmation } from '@/app_components/SignIn/Confirmations/SignInPasswordlessConfirmation';
import { SignInPasswordlessNewAccountConfirmation } from '@/app_components/SignIn/Confirmations/SignInPasswordlessNewAccountConfirmation';
import { SignInCreateAccountForm } from '@/app_components/SignIn/Forms/SignInCreateAccountForm';
import { SignInInitialEmailForm } from '@/app_components/SignIn/Forms/SignInInitialEmailForm';
import { SignInPasswordedForm } from '@/app_components/SignIn/Forms/SignInPasswordedForm';
import { SocialSignIn } from '@/app_components/SignIn/SSO/SocialSignIn';
import { CookieService } from '@/app_lib/CookieService';
import { defaultPianoPageConfig } from '@/app_lib/defaults';
import { useUser } from '@/app_lib/providers/UserProvider';
import {
  type SignInStatus,
  SOCIAL_LOGIN_STATUSES,
  type SocialUrls,
  type UserConsentLocation,
} from '@/app_lib/types/accounts/signin';
import { cn } from '@/app_lib/utils/utils';

interface SignInFlowManagerProps {
  socialUrls: SocialUrls;
  consentLocation: UserConsentLocation;
  /** Validated redirect path from server; client saves to cookie and cleans URL */
  signinRedirect?: string | null;
}

export const SignInFlowManager = ({ socialUrls, consentLocation, signinRedirect }: SignInFlowManagerProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { setPianoPageConfig } = useUser();

  const [socialLoginLoading, setSocialLoginLoading] = useState(Boolean(searchParams?.get('social_response_id')));
  const [signInStatus, setSignInStatus] = useState<SignInStatus>('Pending');
  const [disableSocialLogins, setDisableSocialLogins] = useState(false);
  const [email, setEmail] = useState('');

  const showSocialLogin = SOCIAL_LOGIN_STATUSES.includes(signInStatus) && Object.keys(socialUrls).length > 0;

  useEffect(() => {
    setPianoPageConfig(defaultPianoPageConfig);
  }, [setPianoPageConfig]);

  useEffect(() => {
    if (!signinRedirect) {
      return;
    }
    CookieService.set('__rdSI', signinRedirect, {
      expires: 2,
      sameSite: 'Lax',
      secure: true,
    });
    const cleanParams = new URLSearchParams();
    searchParams?.forEach((value, key) => {
      if (key !== 'signin_redirect') {
        cleanParams.set(key, value);
      }
    });
    const cleanUrl = cleanParams.toString() ? `${pathname ?? '/signin'}?${cleanParams}` : (pathname ?? '/signin');
    router.replace(cleanUrl);
  }, [signinRedirect, pathname, router, searchParams]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Scroll on mode change
  useEffect(() => {
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }, [signInStatus]);

  const handleEditEmailClick = () => {
    setSignInStatus('Pending');
    setEmail('');
  };

  const renderContent = () => {
    switch (signInStatus) {
      case 'Passworded':
        return (
          <SignInPasswordedForm
            disabled={socialLoginLoading}
            email={email}
            onEditEmailClick={handleEditEmailClick}
            setDisableSocialLogins={setDisableSocialLogins}
          />
        );
      case 'Passwordless':
        return <SignInPasswordlessConfirmation email={email} />;
      case 'Creating':
        return (
          <SignInCreateAccountForm
            consentLocation={consentLocation}
            disabled={socialLoginLoading}
            email={email}
            onEditEmailClick={handleEditEmailClick}
            onSuccess={() => setSignInStatus('PasswordlessNewAccount')}
            setDisableSocialLogins={setDisableSocialLogins}
          />
        );
      case 'PasswordlessNewAccount':
        return <SignInPasswordlessNewAccountConfirmation email={email} />;
      case 'Success':
        return <SignInAccountCreationConfirmation />;
      default:
        return (
          <SignInInitialEmailForm
            disabled={socialLoginLoading}
            setDisableSocialLogins={setDisableSocialLogins}
            setEmail={setEmail}
            setSignInStatus={setSignInStatus}
          />
        );
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col layout-gap-sm items-center justify-center w-full font-graphik-compact',
        signInStatus === 'Success' ? 'max-w-[400px]' : 'max-w-[340px]',
      )}
    >
      {renderContent()}
      {showSocialLogin && (
        <SocialSignIn
          disableSocialLogins={disableSocialLogins}
          setSignInStatus={setSignInStatus}
          setSocialLoginLoading={setSocialLoginLoading}
          socialLoginLoading={socialLoginLoading}
          socialUrls={socialUrls}
        />
      )}
    </div>
  );
};
